import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

export const useTaskData = (task) => {
  const { user } = useAuth();

  // Fetch document details with fallback to task data
  const {
    data: documentDetails,
    isLoading: documentLoading,
    error: documentError
  } = useQuery({
    queryKey: ['document', task?.document?.id || task?.document_id],
    queryFn: async () => {
      if (!task?.document?.id && !task?.document_id) return null;
      
      const documentId = task.document?.id || task.document_id;
      
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/documents/${documentId}`,
          { headers: { Authorization: `Bearer ${user.token}` }}
        );
        
        // Handle different API response formats
        return response.data.document || response.data.data || response.data;
      } catch (error) {
        // If permission denied (403), fall back to task document data
        if (error.response?.status === 403) {
          console.log('Permission denied for document, using task data as fallback');
          console.log('Task data for fallback:', task);
          console.log('Task document data:', task.document);
          
          // Enhanced fallback with better field mapping
          const fallbackDocument = {
            id: task.document_id || task.document?.id,
            title: task.document?.title || task.document_title || task.title || 'Document',
            reference_number: task.document?.reference_number || task.document_reference || task.reference_number || '',
            type: task.document?.type || task.document_type || task.type || '',
            document_date: task.document?.document_date || task.document_date || '',
            priority: task.document?.priority || task.document_priority || task.priority,
            description: task.document?.description || task.document_description || task.description || '',
            archive_number: task.document?.archive_number || task.archive_number || '',
            archive_registration_date: task.document?.archive_registration_date || task.archive_registration_date || null,
            source_type: task.document?.source_type || task.source_type || '',
            number_of_pages: task.document?.number_of_pages || task.number_of_pages || '',
            is_confidential: task.document?.is_confidential || task.is_confidential || false,
            is_archived: task.document?.is_archived || task.is_archived || false,
            external_source_name: task.document?.external_source_name || task.external_source_name || '',
            status: task.document?.status || task.status || '',
            created_by: task.document?.created_by || task.created_by || '',
            // Additional fields that might be available
            created_at: task.document?.created_at || task.created_at,
            updated_at: task.document?.updated_at || task.updated_at
          };
          
          console.log('Created fallback document:', fallbackDocument);
          return fallbackDocument;
        }
        throw error;
      }
    },
    enabled: !!user?.token && !!(task?.document?.id || task?.document_id),
    staleTime: 5 * 60 * 1000,
    retry: 1
  });

  // Fetch document activities/history with fallback
  const {
    data: documentHistory,
    isLoading: historyLoading,
    error: historyError
  } = useQuery({
    queryKey: ['document-activities', task?.document?.id || task?.document_id],
    queryFn: async () => {
      if (!task?.document?.id && !task?.document_id) return [];
      
      const documentId = task.document?.id || task.document_id;
      
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/documents/${documentId}/activities`,
          { headers: { Authorization: `Bearer ${user.token}` }}
        );
        
        const activities = response.data?.activities || response.data?.data || response.data || [];
        
        // Process and sort activities
        return activities
          .map(activity => {
            let metadata = {};
            if (activity.metadata) {
              try {
                metadata = typeof activity.metadata === 'string' 
                  ? JSON.parse(activity.metadata) 
                  : activity.metadata;
              } catch (e) {
                console.error('Error parsing activity metadata:', e);
              }
            }
            
            // Determine the actual activity type and action
            const activityType = activity.activity_type || activity.type || 'UNKNOWN';
            const action = metadata.action || '';
            
            // Extract forwarding information
            let forwardInfo = {};
            if (activityType === 'FORWARDED' || action === 'TASK_FORWARDED') {
              // Resolve user IDs to names
              const toUserId = metadata.to || metadata.to_user_id;
              const toUserName = toUserId && userIdToName[toUserId] ? userIdToName[toUserId] : metadata.to;
              
              forwardInfo = {
                from: metadata.from || metadata.user_name || activity.user?.full_name || activity.user?.username || '',
                to: toUserName || metadata.to || ''
              };
            }
            
            // Extract assignment information
            let assignmentInfo = {};
            if (activityType === 'ASSIGNED' || action === 'TASK_ASSIGNED') {
              // Resolve assignee ID to name
              const assigneeId = metadata.assignee_id || metadata.to_user_id;
              const assigneeName = assigneeId && userIdToName[assigneeId] ? userIdToName[assigneeId] : 
                                  metadata.assignee_name || metadata.to || '';
              
              assignmentInfo = {
                assignee_name: assigneeName,
                assignee_id: assigneeId || ''
              };
            }
            
            // Process completion information
            let completionInfo = {};
            if (activityType === 'COMPLETED' || action === 'TASK_COMPLETED' || 
                (activityType === 'STATUS_CHANGED' && metadata.status === 'COMPLETED')) {
              completionInfo = {
                completed_by: metadata.user_name || activity.user?.full_name || activity.user?.username || '',
                completion_notes: metadata.notes || metadata.reason || metadata.remarks || '',
                completion_time: activity.created_at || activity.timestamp
              };
            }
            
            // Process receipt information
            let receiptInfo = {};
            if (activityType === 'RECEIVED' || action === 'DOCUMENT_RECEIVED') {
              receiptInfo = {
                received_by: metadata.user_name || activity.user?.full_name || activity.user?.username || '',
                receipt_time: activity.created_at || activity.timestamp
              };
            }
            
            return {
              id: activity.id,
              timestamp: activity.created_at || activity.timestamp,
              type: activityType,
              action: action,
              user: activity.user,
              note: activity.description || activity.note,
              metadata,
              userName: metadata.user_name || activity.user?.full_name || activity.user?.username || '',
              reason: metadata.reason || metadata.notes || metadata.remarks || '',
              forwardInfo,
              assignmentInfo,
              completionInfo,
              receiptInfo,
              isTaskActivity: metadata.task_id === task?.id || activity.task_id === task?.id,
              // Additional fields for better display
              displayType: activityType === 'STATUS_CHANGED' && metadata.status === 'COMPLETED' ? 'COMPLETED' : activityType,
              status: metadata.status,
              previousStatus: metadata.previous_status
            };
          })
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      } catch (error) {
        // If permission denied (403), return empty array
        if (error.response?.status === 403) {
          console.log('Permission denied for document activities, returning empty array');
          return [];
        }
        throw error;
      }
    },
    enabled: !!user?.token && !!(task?.document?.id || task?.document_id),
    staleTime: 2 * 60 * 1000,
    retry: 1
  });

  // Fetch document attachments with fallback
  const {
    data: documentAttachments,
    isLoading: attachmentsLoading,
    error: attachmentsError
  } = useQuery({
    queryKey: ['document-attachments', task?.document?.id || task?.document_id],
    queryFn: async () => {
      if (!task?.document?.id && !task?.document_id) return [];
      
      const documentId = task.document?.id || task.document_id;
      
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/documents/${documentId}/attachments`,
          { headers: { Authorization: `Bearer ${user.token}` }}
        );
        
        return response.data?.attachments || response.data?.data || response.data || [];
      } catch (error) {
        // If permission denied (403), return empty array
        if (error.response?.status === 403) {
          console.log('Permission denied for document attachments, returning empty array');
          return [];
        }
        throw error;
      }
    },
    enabled: !!user?.token && !!(task?.document?.id || task?.document_id),
    staleTime: 10 * 60 * 1000,
    retry: 1
  });

  // Fetch workflow steps with fallback
  const {
    data: workflowSteps,
    isLoading: stepsLoading,
    error: stepsError
  } = useQuery({
    queryKey: ['workflow-steps', task?.document?.id || task?.document_id],
    queryFn: async () => {
      if (!task?.document?.id && !task?.document_id) return [];
      
      const documentId = task.document?.id || task.document_id;
      
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/documents/${documentId}/workflow-steps`,
          { headers: { Authorization: `Bearer ${user.token}` }}
        );
        
        return response.data?.data || response.data?.steps || response.data || [];
      } catch (error) {
        // If permission denied (403), return empty array
        if (error.response?.status === 403) {
          console.log('Permission denied for workflow steps, returning empty array');
          return [];
        }
        throw error;
      }
    },
    enabled: !!user?.token && !!(task?.document?.id || task?.document_id),
    staleTime: 5 * 60 * 1000,
    retry: 1
  });

  // Fetch users for forwarding and name resolution
  const {
    data: users,
    isLoading: usersLoading,
    error: usersError
  } = useQuery({
    queryKey: ['users-all'],
    queryFn: async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/users?limit=1000&page=1`,
        { headers: { Authorization: `Bearer ${user.token}` }}
      );
      
      return response.data?.users || response.data?.data || response.data || [];
    },
    enabled: !!user?.token,
    staleTime: 10 * 60 * 1000,
    retry: 1
  });

  // Create a map for quick user ID to name lookup
  const userIdToName = users?.reduce((acc, user) => {
    acc[user.id] = user.full_name || user.username;
    return acc;
  }, {}) || {};

  // Fetch departments for forwarding
  const {
    data: departments,
    isLoading: departmentsLoading,
    error: departmentsError
  } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/departments`,
        { headers: { Authorization: `Bearer ${user.token}` }}
      );
      
      return response.data?.data || response.data?.departments || response.data || [];
    },
    enabled: !!user?.token,
    staleTime: 10 * 60 * 1000,
    retry: 1
  });

  return {
    documentDetails: documentDetails || task?.document || {},
    documentHistory: documentHistory || [],
    documentAttachments: documentAttachments || [],
    workflowSteps: workflowSteps || [],
    users: users || [],
    departments: departments || [],
    loading: {
      document: documentLoading,
      history: historyLoading,
      attachments: attachmentsLoading,
      steps: stepsLoading,
      users: usersLoading,
      departments: departmentsLoading
    },
    errors: {
      document: documentError,
      history: historyError,
      attachments: attachmentsError,
      steps: stepsError,
      users: usersError,
      departments: departmentsError
    }
  };
}; 